import {
  IconAmbulance,
  IconBook2,
  IconBabyCarriage,
  IconBed,
  IconBrain,
  IconBuildingHospital,
  IconBulb,
  IconBuildingStore,
  IconCalendar,
  IconCalendarUser,
  IconCash,
  IconChartBar,
  IconClipboardHeart,
  IconDoorEnter,
  IconFileInvoice,
  IconFlask,
  IconFolder,
  IconFolderHeart,
  IconHeartbeat,
  IconHistory,
  IconHome,
  IconLayoutDashboard,
  IconListDetails,
  IconMessage2,
  IconMoneybag,
  IconNotebook,
  IconPackages,
  IconPalette,
  IconPercentage,
  IconPill,
  IconReceipt2,
  IconReportAnalytics,
  IconReportMoney,
  IconScan,
  IconSettings,
  IconShieldCheck,
  IconShoppingCart,
  IconStethoscope,
  IconTable,
  IconUserCog,
  IconUsers,
  IconUsersGroup,
  IconWallet,
} from '@tabler/icons-react';

export type NavIcon = typeof IconHome;

export interface NavItem {
  key: string;
  to: string;
  label: string;
  /** Court descriptif affiché dans la grande fenêtre de la rubrique. */
  desc: string;
  icon: NavIcon;
  /** Couleur de la pastille : elle donne le sens du menu. */
  color: string;
  anyOf?: string[];
  /** Module vendu séparément : le menu est masqué s'il n'est pas dans l'abonnement. */
  module?: string;
}

export interface NavGroup {
  title: string;
  /** Nombre affiché devant l'icône à la place du nombre de menus (ex. dossiers patients). */
  countOf?: 'patients';
  icon: NavIcon;
  color: string;
  items: NavItem[];
}

export const DASHBOARD: NavItem = {
  key: 'dashboard',
  to: '/',
  label: 'Tableau de bord',
  desc: 'Activité du jour',
  icon: IconLayoutDashboard,
  color: '#2563eb',
  anyOf: ['dashboard.view'],
};

/** Rubriques du menu latéral ; chacune ouvre sa grande fenêtre de sous-menus. */
export const NAV_GROUPS: NavGroup[] = [
  {
    title: 'Fichier',
    icon: IconFolder,
    color: '#b45309',
    items: [
      {
        key: 'fichier-listes',
        to: '/fichier/listes',
        label: 'Listes des formulaires',
        desc: 'Formes galéniques, unités, liens de parenté, maternité…',
        icon: IconListDetails,
        color: '#b45309',
        anyOf: ['referentials.manage'],
      },
      {
        key: 'fichier-structure',
        to: '/fichier/titres-services',
        label: 'Titres et services',
        desc: 'Titres (Docteur, Professeur, Sage-femme…), services et types de service',
        icon: IconBuildingHospital,
        color: '#92400e',
        anyOf: ['referentials.manage', 'admin.settings.manage'],
      },
      {
        key: 'fichier-tables',
        to: '/fichier/tables',
        label: 'Tables de référence',
        desc: 'Catégories, moyens de paiement, spécialités, types de documents',
        icon: IconTable,
        color: '#0e7490',
        anyOf: ['catalog.manage', 'admin.settings.manage', 'cashdesk.supervise', 'finance.manage'],
      },
    ],
  },
  {
    title: 'Dossiers patients',
    countOf: 'patients',
    icon: IconFolderHeart,
    color: '#be123c',
    items: [
      {
        key: 'dossiers',
        to: '/dossiers',
        label: 'Dossiers patients',
        desc: 'Parcours complet, traitements, résultats et points à vérifier',
        icon: IconFolderHeart,
        color: '#be123c',
        anyOf: ['medical_files.view'],
      },
      {
        key: 'gdt',
        to: '/gdt',
        label: 'Guide GDT 2008',
        desc: 'Guide de diagnostic et de traitement : sommaire, recherche, pages du document',
        icon: IconBook2,
        color: '#0f766e',
        anyOf: ['gdt.view'],
        module: 'GDT',
      },
    ],
  },
  {
    title: 'Accueil',
    icon: IconDoorEnter,
    color: '#0e7765',
    items: [
      {
        key: 'accueil',
        to: '/accueil',
        label: 'Accueil & file',
        desc: 'Arrivées, file d’attente et appel des patients',
        icon: IconDoorEnter,
        color: '#0e7765',
        anyOf: ['encounters.view'],
      },
      {
        key: 'agenda',
        to: '/agenda',
        label: 'Rendez-vous',
        desc: 'Agenda des professionnels de santé, prise et suivi des rendez-vous',
        icon: IconCalendar,
        color: '#dc2626',
        anyOf: ['appointments.view'],
      },
      {
        key: 'patients',
        to: '/patients',
        label: 'Patients',
        desc: 'Dossiers, identité, antécédents et documents',
        icon: IconUsers,
        color: '#7c3aed',
        anyOf: ['patients.view'],
      },
    ],
  },
  {
    title: 'Soins',
    icon: IconStethoscope,
    color: '#0891b2',
    items: [
      {
        key: 'consultations',
        to: '/consultations',
        label: 'Patients en cours',
        desc: 'Consultations, prescriptions et examens demandés',
        icon: IconStethoscope,
        color: '#0891b2',
        anyOf: ['consultations.manage', 'prescriptions.manage'],
        module: 'CONSULTATIONS',
      },
      {
        key: 'urgences',
        to: '/urgences',
        label: 'Urgences',
        desc: 'Tri, niveaux de priorité et prise en charge',
        icon: IconHeartbeat,
        color: '#dc2626',
        anyOf: ['triage.manage'],
        module: 'URGENCES',
      },
      {
        key: 'soins',
        to: '/soins',
        label: 'Soins infirmiers',
        desc: 'Plans de soins, administrations et constantes',
        icon: IconClipboardHeart,
        color: '#db2777',
        anyOf: ['nursing.manage'],
        module: 'CONSULTATIONS',
      },
      {
        key: 'hospitalisation',
        to: '/hospitalisation',
        label: 'Hospitalisation',
        desc: 'Lits, admissions, transferts et sorties',
        icon: IconBed,
        color: '#1d4ed8',
        anyOf: ['inpatient.view', 'inpatient.admit', 'inpatient.transfer', 'inpatient.discharge'],
        module: 'HOSPITALISATION',
      },
      {
        key: 'maternite',
        to: '/maternite',
        label: 'Maternité',
        desc: 'Grossesses, visites prénatales, accouchements',
        icon: IconBabyCarriage,
        color: '#be123c',
        anyOf: ['maternity.view', 'maternity.manage'],
        module: 'MATERNITE',
      },
      {
        key: 'ambulance',
        to: '/ambulance',
        label: 'Ambulance',
        desc: 'Régulation des missions, équipages et véhicules',
        icon: IconAmbulance,
        color: '#c2410c',
        anyOf: ['ambulance.view', 'ambulance.dispatch', 'ambulance.crew', 'ambulance.fleet'],
        module: 'AMBULANCE',
      },
    ],
  },
  {
    title: 'Plateaux techniques',
    icon: IconFlask,
    color: '#ea580c',
    items: [
      {
        key: 'pharmacie',
        to: '/pharmacie',
        label: 'Pharmacie',
        desc: 'Délivrance des ordonnances et ventes',
        icon: IconPill,
        color: '#db2777',
        anyOf: ['pharmacy.dispense'],
        module: 'PHARMACIE',
      },
      {
        key: 'laboratoire',
        to: '/laboratoire',
        label: 'Laboratoire',
        desc: 'Prélèvements, résultats et validation',
        icon: IconFlask,
        color: '#ea580c',
        anyOf: ['lab.collect', 'lab.enter', 'lab.validate'],
        module: 'LABORATOIRE',
      },
      {
        key: 'imagerie',
        to: '/imagerie',
        label: 'Imagerie',
        desc: 'Planification, réalisation et comptes rendus',
        icon: IconScan,
        color: '#0f766e',
        anyOf: ['imaging.view', 'imaging.schedule', 'imaging.perform', 'imaging.report'],
        module: 'IMAGERIE',
      },
      {
        key: 'stock',
        to: '/stock',
        label: 'Stocks',
        desc: 'Articles, lots, mouvements et inventaires',
        icon: IconPackages,
        color: '#ca8a04',
        anyOf: ['stock.view', 'stock.manage', 'stock.count'],
        module: 'PHARMACIE',
      },
      {
        key: 'achats',
        to: '/achats',
        label: 'Achats',
        desc: 'Fournisseurs, commandes, réceptions et factures',
        icon: IconShoppingCart,
        color: '#9333ea',
        anyOf: ['procurement.request', 'procurement.manage', 'procurement.approve'],
        module: 'ACHATS',
      },
    ],
  },
  {
    title: 'Finances',
    icon: IconWallet,
    color: '#16a34a',
    items: [
      {
        key: 'facturation',
        to: '/facturation',
        label: 'Facturation',
        desc: 'Factures des patients et parts assurées',
        icon: IconFileInvoice,
        color: '#2563eb',
        anyOf: ['billing.view'],
        module: 'FACTURATION',
      },
      {
        key: 'caisse',
        to: '/caisse',
        label: 'Caisse',
        desc: 'Encaissements, avances, remboursements et clôture',
        icon: IconCash,
        color: '#16a34a',
        anyOf: ['cashdesk.operate', 'cashdesk.supervise', 'refunds.approve'],
        module: 'FACTURATION',
      },
      {
        key: 'assurances',
        to: '/assurances',
        label: 'Assurances',
        desc: 'Assureurs, contrats, adhérents et bordereaux',
        icon: IconShieldCheck,
        color: '#0284c7',
        anyOf: ['insurance.view', 'insurance.manage', 'insurance.coverage', 'insurance.claims'],
        module: 'ASSURANCES',
      },
      {
        key: 'finances',
        to: '/finances',
        label: 'Dépenses & recettes',
        desc: 'Dépenses, autres recettes et trésorerie',
        icon: IconReportMoney,
        color: '#b45309',
        anyOf: ['finance.view', 'finance.manage', 'finance.approve'],
        module: 'FINANCES',
      },
      {
        key: 'journaux',
        to: '/journaux',
        label: 'Journal recettes & dépenses',
        desc: 'Montants du jour par rubrique, états par jour, semaine, mois ou période',
        icon: IconNotebook,
        color: '#0f766e',
        anyOf: ['journals.view', 'journals.manage'],
        module: 'JOURNAUX',
      },
      {
        key: 'prestataires',
        to: '/prestataires',
        label: 'Paie des prestataires',
        desc: 'Partage des recettes du jour, prélèvement, bulletins et paiement en caisse',
        icon: IconMoneybag,
        color: '#7c2d12',
        anyOf: ['providers.view', 'providers.manage', 'providers.pay'],
        module: 'PRESTATAIRES',
      },
      {
        key: 'retenues',
        to: '/retenues-salaires',
        label: 'Retenues sur salaires',
        desc: 'État mensuel : salaires bruts et nets, retenue calculée',
        icon: IconPercentage,
        color: '#be185d',
        anyOf: ['journals.payroll'],
        module: 'JOURNAUX',
      },
    ],
  },
  {
    title: 'Pilotage',
    icon: IconChartBar,
    color: '#1d4ed8',
    items: [
      {
        key: 'rapports',
        to: '/rapports',
        label: 'Rapports',
        desc: 'Indicateurs d’activité, médicaux et financiers',
        icon: IconChartBar,
        color: '#1d4ed8',
        anyOf: ['reports.activity', 'reports.medical', 'reports.finance', 'reports.hr'],
        module: 'RAPPORTS',
      },
      {
        key: 'rapports-activite',
        to: '/rapports-activite',
        label: 'Rapports d’activité',
        desc: 'Rapports périodiques par profil : saisie, soumission et impression',
        icon: IconReportAnalytics,
        color: '#7c3aed',
        anyOf: ['activity_reports.own', 'activity_reports.view_all'],
        module: 'RAPPORTS',
      },
      {
        key: 'personnel',
        to: '/personnel',
        label: 'Personnel et planning',
        desc: 'Fiches du personnel, gardes et absences',
        icon: IconCalendarUser,
        color: '#0891b2',
        anyOf: ['hr.view', 'hr.manage', 'hr.planning', 'hr.planning.view', 'hr.leave.approve'],
        module: 'PERSONNEL',
      },
    ],
  },
  {
    title: 'Administration',
    icon: IconSettings,
    color: '#475569',
    items: [
      {
        key: 'utilisateurs',
        to: '/admin/utilisateurs',
        label: 'Utilisateurs',
        desc: 'Comptes, rôles attribués et accès',
        icon: IconUserCog,
        color: '#475569',
        anyOf: ['admin.users.manage'],
      },
      {
        key: 'roles',
        to: '/admin/roles',
        label: 'Rôles et droits',
        desc: 'Permissions regroupées par rôle',
        icon: IconUsersGroup,
        color: '#9333ea',
        anyOf: ['admin.roles.manage'],
      },
      {
        key: 'praticiens',
        to: '/admin/praticiens',
        label: 'Professionnels de santé & horaires',
        desc: 'Professionnels de santé, services et plages de consultation',
        icon: IconStethoscope,
        color: '#0891b2',
        anyOf: ['schedules.manage'],
      },
      {
        key: 'catalogue',
        to: '/admin/catalogue',
        label: 'Actes & tarifs',
        desc: 'Actes facturables et grilles de prix',
        icon: IconReceipt2,
        color: '#ea580c',
        anyOf: ['catalog.manage'],
      },
      {
        key: 'lits',
        to: '/admin/lits',
        label: 'Chambres et lits',
        desc: 'Services, chambres et lits d’hospitalisation',
        icon: IconBed,
        color: '#1d4ed8',
        anyOf: ['inpatient.config'],
        module: 'HOSPITALISATION',
      },
      {
        key: 'structure',
        to: '/admin/structure',
        label: 'Structure',
        desc: 'Sites, services, spécialités et paramètres',
        icon: IconBuildingHospital,
        color: '#92400e',
        anyOf: ['admin.settings.manage'],
      },
      {
        key: 'notifications',
        to: '/admin/notifications',
        label: 'Notifications',
        desc: 'Modèles de SMS et e-mails, suivi des envois',
        icon: IconMessage2,
        color: '#0f766e',
        anyOf: ['notifications.manage'],
        module: 'NOTIFICATIONS',
      },
      {
        key: 'parametres',
        to: '/admin/parametres',
        label: 'Paramètres',
        desc: 'En-tête, pied de page et informations légales',
        icon: IconSettings,
        color: '#0f766e',
        anyOf: ['admin.settings.manage'],
      },
      {
        key: 'apparence',
        to: '/admin/apparence',
        label: 'Apparence',
        desc: 'Thème, couleurs, police et taille du texte',
        icon: IconPalette,
        color: '#db2777',
        anyOf: ['admin.settings.manage'],
      },
      {
        key: 'audit',
        to: '/admin/audit',
        label: "Journal d'audit",
        desc: 'Traçabilité des accès et des modifications',
        icon: IconHistory,
        color: '#64748b',
        anyOf: ['audit.view'],
      },
      {
        key: 'journal-ia',
        to: '/admin/journal-ia',
        label: 'Journal de l’IA',
        desc: 'Demandes à GestClinic AI, décisions des professionnels et avis',
        icon: IconBrain,
        color: '#7c3aed',
        anyOf: ['assistant.history'],
        module: 'IA',
      },
    ],
  },
];

/** Espace Éditeur : visible seulement des comptes qui ont la permission (espace Éditeur). */
NAV_GROUPS.push({
  title: 'Éditeur',
  icon: IconBuildingStore,
  color: '#0f766e',
  items: [
    {
      key: 'editeur',
      to: '/editeur',
      label: 'Cliniques clientes',
      desc: 'Abonnements, modules, essais et suspension des cliniques',
      icon: IconBuildingStore,
      color: '#0f766e',
      anyOf: ['editor.manage'],
    },
    {
      key: 'editeur-gdt',
      to: '/editeur/gdt',
      label: 'Sources médicales (GDT)',
      desc: 'Relecture, validation médicale et publication du GDT 2008',
      icon: IconBook2,
      color: '#0f766e',
      anyOf: ['editor.manage', 'editor.gdt.validate'],
    },
    {
      key: 'editeur-ia',
      to: '/editeur/ia',
      label: 'GestClinic AI',
      desc: 'Fournisseur d’IA : Claude, ChatGPT, Gemini, DeepSeek ou modèle local',
      icon: IconBrain,
      color: '#7c3aed',
      anyOf: ['editor.manage'],
    },
    {
      key: 'editeur-recommandations',
      to: '/editeur/recommandations',
      label: 'Recommandations',
      desc: 'Propositions d’amélioration envoyées par les utilisateurs des cliniques',
      icon: IconBulb,
      color: '#ca8a04',
      anyOf: ['editor.manage'],
    },
  ],
});

export const ALL_ITEMS: NavItem[] = [DASHBOARD, ...NAV_GROUPS.flatMap((g) => g.items)];

/** Menu correspondant à l'adresse courante (préfixe le plus long). */
export function findNavItem(pathname: string): NavItem | undefined {
  if (pathname === '/') return DASHBOARD;
  return ALL_ITEMS.filter((i) => i.to !== '/' && pathname.startsWith(i.to)).sort(
    (a, b) => b.to.length - a.to.length,
  )[0];
}
